import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { BASE_URL } from "../../../const";
import Navbar from "../../../components/Navbar";
import { QRCodeSVG } from "qrcode.react";

/* ---------- Helpers: image compression + preview ---------- */
const compressImage = (file, maxWidth = 1200, quality = 0.7) =>
  new Promise((resolve) => {
    if (!file || !file.type?.startsWith?.("image/")) return resolve(file);
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxWidth / img.width);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => {
            if (!blob) return resolve(file);
            const compressed = new File([blob], file.name.replace(/\.\w+$/, ".jpg"), {
              type: "image/jpeg",
              lastModified: Date.now(),
            });
            resolve(compressed);
          },
          "image/jpeg",
          quality
        );
      };
      img.onerror = () => resolve(file);
      img.src = reader.result;
    };
    reader.onerror = () => resolve(file);
    reader.readAsDataURL(file);
  });

const makePreviewEntries = async (fileList) => {
  const files = Array.from(fileList || []);
  const processed = await Promise.all(
    files.map(async (f) => {
      const compressed = await compressImage(f);
      return {
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        file: compressed,
        preview: URL.createObjectURL(compressed),
        name: compressed.name,
        type: compressed.type,
      };
    })
  );
  return processed;
};

/* ---------- Helpers: convert File <-> storable base64 ---------- */
const fileToDataURL = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error("file read error"));
    r.readAsDataURL(file);
  });

const imageEntryToStorable = async (imgEntry) => {
  const data = await fileToDataURL(imgEntry.file);
  return {
    id: imgEntry.id,
    name: imgEntry.name || imgEntry.file?.name || "img",
    type: imgEntry.type || imgEntry.file?.type || "image/jpeg",
    data, // base64 dataURL
  };
};

const storableToImageEntry = async (stored) => {
  if (!stored?.data) return null;
  const res = await fetch(stored.data);
  const blob = await res.blob();
  const file = new File([blob], stored.name, { type: stored.type });
  return {
    id: stored.id,
    file,
    preview: URL.createObjectURL(file),
    name: stored.name,
    type: stored.type,
  };
};

/* ---------- Component ---------- */
const STORAGE_PREFIX = "pending_batch_layer";

const BatchSubmissionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const layerParam = params.get("layer");
  const sku = params.get("sku");
  const plantNameParam = params.get("plantName") || "";

  const storageKey = layerParam && sku ? `${STORAGE_PREFIX}_${layerParam}_${sku}` : null;

  // core state
  const [batchData, setBatchData] = useState(null);
  const [plantName, setPlantName] = useState(plantNameParam);
  const [finalQty, setFinalQty] = useState("");
  const [rowLoc, setRowLoc] = useState("");
  const [sectionLoc, setSectionLoc] = useState("");
  const [images, setImages] = useState([]); // initial images
  const [submissionImages, setSubmissionImages] = useState([]);
  const [batchResponse, setBatchResponse] = useState(null);
  const [creating, setCreating] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [task_ids, setTasks] = useState([])
  // upstream
  const [upstreamBatches, setUpstreamBatches] = useState([]);
  const [upstreamLoading, setUpstreamLoading] = useState(false);
  const [upstreamError, setUpstreamError] = useState(null);
  const [batchActions, setBatchActions] = useState({}); // { batchId: { transferQty, rejectQty, locked, rejectionImages: [entry...] } }

  // batch upstream pop 
  const [showBatchPopup, setShowBatchPopup] = useState(false);

  const qrCanvasRef = useRef(null);
  const msgTimerRef = useRef(null);

  const numericLayer = useMemo(() => {
    const n = Number(batchData?.layer ?? layerParam);
    return Number.isFinite(n) ? n : null;
  }, [batchData, layerParam]);

  const upstreamLayer = useMemo(() => {
    if (numericLayer == null) return null;
    return numericLayer >= 3 ? null : numericLayer + 1;
  }, [numericLayer]);

  const isUpstreamControlled = Boolean(upstreamLayer);

  /* ---------- message helper ---------- */
  const showMessage = (type, text) => {
    setFeedback({ type, text });
    if (msgTimerRef.current) clearTimeout(msgTimerRef.current);
    msgTimerRef.current = setTimeout(() => setFeedback(null), 3500);
  };

  /* ---------- load draft including saved initial images & upstream selections ---------- */
  useEffect(() => {
    if (!layerParam || !sku) {
      navigate("/");
      return;
    }
    if (!storageKey) return;

    (async () => {
      try {
        const raw = localStorage.getItem(storageKey);
        console.log("raw",raw)
        if (!raw) return;
        const parsed = JSON.parse(raw);

        setBatchData(parsed);
        setPlantName(parsed.plantName || plantNameParam || "");
        setFinalQty(parsed.finalQty ? String(parsed.finalQty) : parsed.totalQty ? String(parsed.totalQty) : "");
        setRowLoc(parsed.rowLoc || "");
        setSectionLoc(parsed.sectionLoc || "");
        setBatchResponse(parsed.batchResponse)
        setTasks(parsed.taskIds || [])


        // Restore initial images if stored
        if (parsed.images && Array.isArray(parsed.images) && parsed.images.length) {
          try {
            const restoredImages = await Promise.all(parsed.images.map(storableToImageEntry));
            setImages(restoredImages.filter(Boolean));
          } catch (e) {
            console.warn("Could not restore initial images", e);
          }
        }

        // Restore upstreamSelections if present (with rejection images)
        if (parsed.upstreamSelections) {
          const restored = {};
          for (const batchId of Object.keys(parsed.upstreamSelections)) {
            const storedAction = parsed.upstreamSelections[batchId];
            const action = {
              transferQty: storedAction.transferQty ?? "",
              rejectQty: storedAction.rejectQty ?? "",
              locked: !!storedAction.locked,
              rejectionImages: [],
            };
            if (storedAction.rejectionImages && Array.isArray(storedAction.rejectionImages)) {
              const restoredImgs = await Promise.all(
                storedAction.rejectionImages.map(async (si) => {
                  try {
                    return await storableToImageEntry(si);
                  } catch {
                    return null;
                  }
                })
              );
              action.rejectionImages = restoredImgs.filter(Boolean);
            }
            restored[batchId] = action;
          }
          setBatchActions(prev => ({ ...restored, ...prev }));
        }
      } catch (e) {
        console.error("Failed to restore draft:", e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layerParam, sku, storageKey]);

  /* ---------- fetch upstream batches and merge with restored actions ---------- */
  useEffect(() => {
    if (!batchData || !upstreamLayer) {
      setUpstreamBatches([]);
      setUpstreamError(null);
      // keep any restored batchActions until they can be merged
      return;
    }

    let mounted = true;
    const controller = new AbortController();

    (async () => {
      setUpstreamLoading(true);
      setUpstreamError(null);
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("Missing authentication token.");
        const params = new URLSearchParams({
          warehouse: batchData.warehouse,
          layer: String(upstreamLayer),
          sku: batchData.plantSku,
        });
        const res = await fetch(`${BASE_URL}/get-active-batches/?${params.toString()}`, {
          headers: { Authorization: `Token ${token}` },
          signal: controller.signal,
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Unable to fetch upstream batches.");
        if (!mounted) return;
        const batches = data.batches || [];
        setUpstreamBatches(batches);
        // Ensure action entries exist for fetched batches
        setBatchActions(prev => {
          const out = { ...prev };
          batches.forEach(b => {
            if (!out[b.batch_id]) {
              out[b.batch_id] = { transferQty: "", rejectQty: "", locked: false, rejectionImages: [] };
            }
          });
          return out;
        });
      } catch (err) {
        if (!mounted) return;
        console.error(err);
        setUpstreamError(err.message || "Unable to fetch upstream batches.");
      } finally {
        if (mounted) setUpstreamLoading(false);
      }
    })();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [batchData, upstreamLayer]);

  /* ---------- derive locked transfer total and auto-fill finalQty when upstream controlled ---------- */
  const lockedTransferTotal = useMemo(() => {
    return Object.values(batchActions).reduce((sum, a) => sum + (Number(a.transferQty) || 0), 0);
  }, [batchActions]);

  useEffect(() => {
    if (isUpstreamControlled) {
      setFinalQty(lockedTransferTotal ? String(lockedTransferTotal) : "");
    }
  }, [isUpstreamControlled, lockedTransferTotal]);

  /* ---------- persist minimal draft + upstreamSelections + initial images to localStorage ---------- */
  useEffect(() => {
    if (!batchData || !storageKey) return;

    let mounted = true;
    (async () => {
      try {
        // serialize initial images
        const storableImages = images && images.length
          ? await Promise.all(images.map(async (img) => {
              try { return await imageEntryToStorable(img); } catch (e) { console.warn("skip storing image", e); return null; }
            })).then(arr => arr.filter(Boolean))
          : [];

        // build serializable upstreamSelections
        const upstreamSelections = {};
        for (const [batchId, action] of Object.entries(batchActions || {})) {
          const entry = {
            transferQty: action.transferQty ?? "",
            rejectQty: action.rejectQty ?? "",
            locked: !!action.locked,
            rejectionImages: [],
          };
          if (action.rejectionImages && action.rejectionImages.length) {
            entry.rejectionImages = await Promise.all(
              action.rejectionImages.map(async (img) => {
                try { return await imageEntryToStorable(img); } catch (e) { console.warn("skip storing rejection image", e); return null; }
              })
            ).then(arr => arr.filter(Boolean));
          }
          upstreamSelections[batchId] = entry;
        }

        const payload = {
          layer: batchData.layer,
          warehouse: batchData.warehouse,
          plantSku: batchData.plantSku,
          plantName: plantName || batchData.plantName,
          totalQty: batchData.totalQty,
          finalQty,
          rowLoc,
          sectionLoc,
          batchResponse,
          taskIds: task_ids,
          images: storableImages,
          upstreamSelections,
        };

        if (!mounted) return;
        localStorage.setItem(storageKey, JSON.stringify(payload));
      } catch (e) {
        console.warn("Unable to persist draft/upstream selections", e);
      }
    })();

    return () => { mounted = false; };
  }, [batchData, plantName, finalQty, rowLoc, sectionLoc, batchResponse, batchActions, images, storageKey]);

  /* ---------- upstream UI handlers ---------- */
  const handleBatchActionChange = (batchId, field, rawValue) => {
    const cleaned = rawValue === "" ? "" : String(rawValue).replace(/[^\d]/g, "");
    setBatchActions(prev => {
      const cur = prev[batchId] || { transferQty: "", rejectQty: "", locked: false, rejectionImages: [] };
      const batchInfo = upstreamBatches.find(b => b.batch_id === batchId) || {};
      const available = Number(batchInfo.avl_qty) || 0;
      const other = field === "transferQty" ? Number(cur.rejectQty) || 0 : Number(cur.transferQty) || 0;
      let numeric = cleaned === "" ? "" : Number(cleaned);
      if (numeric !== "" && available) {
        const maxAllowed = Math.max(available - other, 0);
        if (numeric > maxAllowed) numeric = maxAllowed;
      }
      return { ...prev, [batchId]: { ...cur, [field]: numeric === "" ? "" : String(numeric) } };
    });
  };

  const handleRejectionImageUpload = async (batchId, files) => {
    if (!files?.length) return;
    const prepared = await makePreviewEntries(files);
    setBatchActions(prev => {
      const cur = prev[batchId] || { transferQty: "", rejectQty: "", locked: false, rejectionImages: [] };
      return { ...prev, [batchId]: { ...cur, rejectionImages: [...(cur.rejectionImages || []), ...prepared] } };
    });
  };

  const removeRejectionImage = (batchId, idx) => {
    setBatchActions(prev => {
      const cur = prev[batchId];
      if (!cur) return prev;
      const nextImgs = (cur.rejectionImages || []).slice();
      const rem = nextImgs[idx];
      if (rem) URL.revokeObjectURL(rem.preview);
      nextImgs.splice(idx, 1);
      return { ...prev, [batchId]: { ...cur, rejectionImages: nextImgs } };
    });
  };

  const toggleBatchLock = (batchId) => {
    setBatchActions(prev => {
      const cur = prev[batchId] || { transferQty: "", rejectQty: "", locked: false, rejectionImages: [] };
      if (!cur.locked) {
        const hasQty = (Number(cur.transferQty) || 0) > 0 || (Number(cur.rejectQty) || 0) > 0;
        if (!hasQty) {
          showMessage("error", "Add transfer or reject qty before locking.");
          return prev;
        }
      }
      return { ...prev, [batchId]: { ...cur, locked: !cur.locked } };
    });
  };

  /* ---------- submit locked source batch adjustments ---------- */
  const submitLockedBatchAdjustments = async (lockedEntries) => {
    if (!lockedEntries.length) return;
    const token = localStorage.getItem("token");
    if (!token) throw new Error("Missing authentication token.");
    for (const { batchId, action } of lockedEntries) {
      const transferQty = Number(action.transferQty) || 0;
      const rejectQty = Number(action.rejectQty) || 0;
      if (!transferQty && !rejectQty) continue;
      const formData = new FormData();
      formData.append("batch_id", batchId);
      formData.append("transfer_qty", String(transferQty));
      formData.append("reject_qty", String(rejectQty));
      (action.rejectionImages || []).forEach(img => formData.append("images", img.file));
      const res = await fetch(`${BASE_URL}/update-batch/`, {
        method: "POST",
        headers: { Authorization: `Token ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.message || "Unable to update source batch.");
    }
  };


  const handleCreateBatchClick = () => {
    const selectedQty = Number(batchData?.totalQty) || 0;
    const finalQtyNum = Number(finalQty) || 0;
    const difference = selectedQty - finalQtyNum;

    if (difference > 20) {
      const ok = window.confirm(
        `You are creating Batch with ${difference} Quantities Less then ${selectedQty} Selected Quantities.\nDo you still want to create the batch?`
      );
      if (!ok) return; // STOP
    }
    if (difference < -20) {
      const ok = window.confirm(
        `You are adding ${-difference} Quantities More then ${selectedQty} Selected Quantities.\nDo you still want to create the batch?`
      );
      if (!ok) return; // STOP
    }

    createBatch(); // Continue normally
  };

  /* ---------- create batch (submit upstream adjustments first if present) ---------- */
  const createBatch = async () => {
    if (!batchData) return showMessage("error", "No batch data.");
    const token = localStorage.getItem("token");
    if (!token) return showMessage("error", "Missing token.");

    if (!finalQty || Number(finalQty) <= 0) return showMessage("error", "Enter final quantity.");
    if (images.length === 0) return showMessage("error", "Add at least one initial image.");
    if (!rowLoc || rowLoc.trim() === "") return showMessage("error", "Provide row location.");

    if (isUpstreamControlled) {
      const locked = Object.entries(batchActions)
        .filter(([, a]) => a.locked && Number(a.transferQty) > 0)
        .map(([batchId, a]) => ({ batchId, action: a }));
      if (!locked.length) return showMessage("error", "Lock at least one source batch with transfer quantity.");
    }

    setCreating(true);
    try {
      if (isUpstreamControlled) {
        const lockedEntries = Object.entries(batchActions)
          .filter(([, a]) => a.locked)
          .map(([batchId, a]) => ({ batchId, action: a }));
        await submitLockedBatchAdjustments(lockedEntries);
      }

      const fd = new FormData();
      fd.append("layer_type", batchData.layer);
      fd.append("warehouse", batchData.warehouse);
      fd.append("total_qty", String(Number(finalQty)));
      fd.append("plant_sku", batchData.plantSku);
      fd.append("row_loc", rowLoc);
      fd.append("section_loc", sectionLoc);
      images.forEach((img) => fd.append("images", img.file));

      const res = await fetch(`${BASE_URL}/create-batch/`, {
        method: "POST",
        headers: { Authorization: `Token ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.message || "Create failed");

      setBatchResponse(data);
      setBatchData(prev => prev ? { ...prev, finalQty: Number(finalQty) } : prev);
      
      const selectedQty = Number(batchData.totalQty) || 0;
      const finalQtyNum = Number(finalQty) || 0;
      const diff = selectedQty - finalQtyNum;

      let successMsg = "Batch created.";
      if (diff > 19) {
        try {
          await createAdjustmentTask(diff);
          successMsg += ` Adjustment task created for ${diff} units.`;
        } catch (taskErr) {
          console.warn("Adjustment task creation failed:", taskErr);
          // Non-blocking: inform user but don't revert batch
          successMsg += ` (Failed to create adjustment task: ${taskErr.message || "unknown error"})`;
        }
      }

    } catch (err) {
      console.error("Batch creation error:", err);
      showMessage("error", err.message || "Unable to create batch.");
    } finally {
      setCreating(false);
    }
  };

  /* ---------- submission & completion ---------- */
  const uploadSubmissionImages = async () => {
    if (!batchResponse?.batch_id || submissionImages.length === 0) return;
    const token = localStorage.getItem("token");
    if (!token) throw new Error("Missing authentication token.");
    const fd = new FormData();
    submissionImages.forEach((img) => fd.append("images", img.file));
    const res = await fetch(`${BASE_URL}/create-batch/add-images/${batchResponse.batch_id}/`, {
      method: "PATCH",
      headers: { Authorization: `Token ${token}` },
      body: fd,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || data.message || "Upload failed");
    return data;
  };


  const createAdjustmentTask = async (differenceQty) => {
    const token = localStorage.getItem("token");
    if (!token) throw new Error("Missing authentication token.");

    const payload = {
      date: new Date().toISOString().split("T")[0],
      stage: String(batchData.layer),
      warehouse: batchData.warehouse || "pune",
      qty: Math.round(differenceQty),
      sku: batchData.plantSku,
    };

    const res = await fetch(`${BASE_URL}/tasks/create_task/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Token ${token}`,
      },
      body: JSON.stringify(payload),
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data.error || data.message || "Unable to create adjustment task.");
    return data;
};


  const completeBatch = async () => {
    if (!batchData?.taskIds || batchData.taskIds.length === 0) {
      return showMessage("error", "No tasks to complete.");
    }
    if (submissionImages.length === 0) {
      return showMessage("error", "Upload at least one submission image.");
    }
    setCompleting(true);
    try {
      await uploadSubmissionImages();
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Missing token.");

      const res = await fetch(`${BASE_URL}/tasks/mark_completed/`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Token ${token}` },
        body: JSON.stringify({ task_ids: task_ids }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.message || "Complete failed");

      if (storageKey) localStorage.removeItem(storageKey);
      showMessage("success", "Tasks marked completed.");
      navigate(`/layer/${batchData.layer}`);
    } catch (err) {
      console.error(err);
      showMessage("error", err.message || "Unable to complete batch.");
    } finally {
      setCompleting(false);
    }
  };

  /* ---------- QR download ---------- */
  const downloadQR = () => {
    if (!batchResponse || !qrCanvasRef.current) return;
    const svgEl = document.getElementById("qr-svg");
    if (!svgEl) return;
    const svgData = new XMLSerializer().serializeToString(svgEl);
    const img = new Image();
    img.onload = () => {
      const canvas = qrCanvasRef.current;
      const w = 400, h = 520;
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#fff"; ctx.fillRect(0,0,w,h);
      const qrSize = 240;
      ctx.drawImage(img, (w-qrSize)/2, 40, qrSize, qrSize);
      ctx.fillStyle = "#111"; ctx.font = "bold 16px system-ui, sans-serif"; ctx.textAlign = "center";
      ctx.fillText(`Batch: ${batchResponse.batch_id}`, w/2, 320);
      ctx.fillText(`SKU: ${batchData.plantSku}`, w/2, 344);
      const a = document.createElement("a");
      a.href = canvas.toDataURL("image/png");
      a.download = `batch_${batchResponse.batch_id}_qr.png`;
      a.click();
    };
    img.src = "data:image/svg+xml;base64," + btoa(svgData);
  };

  /* ---------- file handlers ---------- */
  const onAddInitialImages = async (e) => {
    const prepared = await makePreviewEntries(e.target.files);
    setImages(p => [...p, ...prepared]);
    e.target.value = "";
  };
  const removeInitialImage = (idx) => {
    setImages(p => {
      const rem = p[idx];
      if (rem) URL.revokeObjectURL(rem.preview);
      return p.filter((_, i) => i !== idx);
    });
  };

  const onAddSubmissionImages = async (e) => {
    const prepared = await makePreviewEntries(e.target.files);
    setSubmissionImages(p => [...p, ...prepared]);
    e.target.value = "";
  };
  const removeSubmissionImage = (idx) => {
    setSubmissionImages(p => {
      const rem = p[idx];
      if (rem) URL.revokeObjectURL(rem.preview);
      return p.filter((_, i) => i !== idx);
    });
  };

  /* ---------- cleanup on unmount ---------- */
  useEffect(() => {
    return () => {
      images.forEach(i => URL.revokeObjectURL(i.preview));
      submissionImages.forEach(i => URL.revokeObjectURL(i.preview));
      Object.values(batchActions).forEach(a => (a.rejectionImages || []).forEach(img => img && URL.revokeObjectURL(img.preview)));
      if (msgTimerRef.current) clearTimeout(msgTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------- UI guard ---------- */
  if (!batchData)
    return (
      <>
        <Navbar />
        <div className="p-6 text-center text-gray-600">Loading or no pending batch found.</div>
      </>
    );

  const selectedQty = Number(batchData.totalQty) || 0;
  const finalQtyNum = Number(finalQty) || 0;
  const difference = finalQty !== "" ? selectedQty - finalQtyNum : null;

return (
  <>
    <Navbar />
    <div className="max-w-3xl mx-auto p-4 space-y-6">
      {feedback && (
        <div
          className={`p-2 rounded text-sm ${
            feedback.type === "error" ? "bg-red-50 text-red-700" : "bg-green-50 text-green-700"
          }`}
          role="status"
        >
          {feedback.text}
        </div>
      )}

      {/* Header card */}
      <div className="bg-white p-4 rounded-lg border shadow-sm">
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-lg">Batch Submission</h2>
            {!batchResponse && (
              <button
                onClick={() => {
                  if (storageKey) localStorage.removeItem(storageKey);
                  navigate(`/layer/${layerParam}`);
                }}
                className="text-sm text-rose-600 hover:text-rose-700 border border-gray-300 hover:border-rose-300 bg-gray-100 hover:bg-rose-50 px-3 py-1 rounded-md transition-colors duration-150"
                aria-label="Clear draft"
              >
                Clear draft
              </button>
            )}
          </div>
          
          <div className="text-sm text-gray-700 flex flex-wrap items-center gap-2">
            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">Layer: {batchData.layer}</span>
            <span className="text-gray-400">•</span>
            <span className="bg-green-100 text-green-800 px-2 py-1 rounded">Plant: {batchData.plantName}</span>
            <span className="text-gray-400">•</span>
            <span>SKU: {batchData.plantSku}</span>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mt-4">
          <div className="p-3 border rounded-lg text-center">
            <div className="text-xs text-gray-500">Selected Task Qty</div>
            <div className="font-semibold text-2xl mt-1">{selectedQty}</div>
          </div>

          <div className="p-3 border-2 border-blue-300 bg-blue-50 rounded-lg text-center">
            <div className="text-xs text-blue-700 font-medium">Batch Creation Qty</div>
            <div className="font-semibold text-2xl mt-1 text-blue-900">{finalQty || "—"}</div>
          </div>

          <div className="p-3 border rounded-lg text-center">
            <div className="text-xs text-gray-500">Difference</div>
            <div className="font-bold text-2xl mt-1">{difference !== null ? difference : "—"}</div>
          </div>
        </div>
      </div>

      {/* Source batches — big full-width button/card */}
      {!batchResponse && isUpstreamControlled && (
      <div className="rounded-lg border p-0 shadow-sm" style={{ backgroundColor: "#f5f5f5" }}>
        <button
          type="button"
          onClick={() => setShowBatchPopup(true)}
          className="w-full text-left p-4 flex items-center justify-between gap-4 focus:outline-none"
          style={{ backgroundColor: "#f5f5f5" }}
        >
          <div className="flex flex-col">
            <h3 className="text-m font-semibold text-gray-900">
              Select Batches from Layer {upstreamLayer}
            </h3>
            <p className="text-xs text-gray-800 mt-1">
              Transfer & Reject inventory from Batches.
            </p>
          </div>

          <span className="text-xs text-gray-900">
            {upstreamBatches?.length ?? 0} batches
          </span>
        </button>
      </div>
      )}


      {/* Source Batches modal */}
      {showBatchPopup && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Source Batches</h3>
                <p className="text-xs text-gray-500 mt-1">Layer {upstreamLayer} · Manage transfers & rejections</p>
              </div>
              <button
                onClick={() => setShowBatchPopup(false)}
                className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600"
                aria-label="Close source batches"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {upstreamLoading ? (
                <div className="py-8 text-center text-sm text-gray-500">Loading upstream batches…</div>
              ) : upstreamError ? (
                <div className="py-6 text-center text-sm text-red-600 bg-red-50 px-4 py-3 rounded-lg">{upstreamError}</div>
              ) : upstreamBatches.length === 0 ? (
                <div className="py-8 text-center text-sm text-gray-500 bg-gray-50 px-4 py-3 rounded-lg">No active batches available</div>
              ) : (
                <div className="grid grid-cols-1 gap-3">
                  {upstreamBatches.map((batch) => {
                    const action = batchActions[batch.batch_id] || { transferQty: "", rejectQty: "", locked: false, rejectionImages: [] };
                    const available = Number(batch.avl_qty) || 0;
                    const transfer = Number(action.transferQty) || 0;
                    const reject = Number(action.rejectQty) || 0;
                    const totalAction = transfer + reject;

                    return (
                      <div
                        key={batch.batch_id}
                        className={`rounded-lg border p-4 transition-all duration-200 ${
                          action.locked ? "border-green-200 bg-green-50/60 shadow-sm" : "border-gray-200 bg-white hover:shadow-md"
                        }`}
                      >
                        {/* header */}
                        <div className="flex items-start justify-between mb-3 gap-4">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">{batch.batch_id}</p>
                            <div className="flex flex-wrap gap-2 mt-2">
                              <span className="inline-flex items-center px-2 py-1 rounded-full bg-blue-100 text-blue-800 text-xs font-medium">
                                Available: {available}
                              </span>
                              {batch.row_loc && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full bg-gray-100 text-gray-700 text-xs">
                                  Bed: {batch.row_loc}
                                </span>
                              )}
                              {batch.section_loc && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full bg-gray-100 text-gray-700 text-xs">
                                  Shed: {batch.section_loc}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* qty inputs */}
                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <div>
                            <label className="text-xs font-medium text-gray-700 block mb-1">Transfer Qty</label>
                            <input
                              type="number"
                              inputMode="numeric"
                              min={0}
                              value={action.transferQty}
                              onChange={(e) => handleBatchActionChange(batch.batch_id, "transferQty", e.target.value)}
                              disabled={action.locked}
                              className="w-full h-9 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                              placeholder="0"
                            />
                          </div>

                          <div>
                            <label className="text-xs font-medium text-gray-700 block mb-1">Reject Qty</label>
                            <input
                              type="number"
                              inputMode="numeric"
                              min={0}
                              value={action.rejectQty}
                              onChange={(e) => handleBatchActionChange(batch.batch_id, "rejectQty", e.target.value)}
                              disabled={action.locked}
                              className="w-full h-9 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                              placeholder="0"
                            />
                          </div>
                        </div>

                        {/* images */}
                        <div className="mb-3">
                          <div className="flex items-center gap-2 mb-2">
                            <input
                              id={`reject-upload-${batch.batch_id}`}
                              type="file"
                              accept="image/*"
                              className="hidden"
                              disabled={action.locked}
                              onChange={(e) => {
                                handleRejectionImageUpload(batch.batch_id, e.target.files);
                                e.target.value = "";
                              }}
                              multiple
                            />
                            <label
                              htmlFor={`reject-upload-${batch.batch_id}`}
                              className={`inline-flex items-center gap-1 text-xs font-medium py-2 px-3 rounded-lg border transition-colors ${
                                action.locked
                                  ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                                  : "bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100 cursor-pointer"
                              }`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              Add Reject Images
                            </label>

                            {(action.rejectionImages || []).length > 0 && (
                              <span className="inline-flex items-center justify-center min-w-6 h-6 px-2 rounded-full bg-blue-100 text-blue-800 text-xs font-medium">
                                {(action.rejectionImages || []).length}
                              </span>
                            )}
                          </div>

                          {(action.rejectionImages || []).length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {action.rejectionImages.map((img, i) => (
                                <div key={i} className="relative group">
                                  <div className="h-16 w-16 rounded-lg border-2 border-gray-200 overflow-hidden">
                                    <img src={img.preview} alt={`Rejection ${i + 1}`} className="h-full w-full object-cover" />
                                  </div>
                                  {!action.locked && (
                                    <button
                                      onClick={() => removeRejectionImage(batch.batch_id, i)}
                                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center opacity-0 group-hover:opacity-100"
                                      aria-label="Remove image"
                                    >
                                      ×
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* lock */}
                        <button
                          onClick={() => toggleBatchLock(batch.batch_id)}
                          disabled={!action.locked && totalAction === 0}
                          className={`w-full py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                            action.locked ? "bg-green-600 text-white shadow-sm" : totalAction > 0 ? "bg-blue-600 text-white hover:bg-blue-700 shadow-sm" : "bg-gray-100 text-gray-400 cursor-not-allowed"
                          }`}
                        >
                          {action.locked ? (
                            <span className="flex items-center justify-center gap-2">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                              </svg>
                              Locked
                            </span>
                          ) : (
                            "Lock Selection"
                          )}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* details & images */}
      {!batchResponse ? (
        <>
          <div className="bg-white p-4 rounded-lg border space-y-3">
            <label className="text-xs">Batch Creation Qty *</label>
            <div className="relative">
              <input
                type="number"
                className="w-full border p-2 rounded-lg pr-28"
                value={finalQty}
                onChange={(e) => {
                  if (!isUpstreamControlled) setFinalQty(e.target.value);
                }}
                min={0}
                readOnly={isUpstreamControlled}
                placeholder={isUpstreamControlled ? "Auto from locked transfers" : "Actual qty"}
              />
              {isUpstreamControlled && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs bg-gray-100 px-2 py-1 rounded">Auto</span>}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs">Bed *</label>
                <input
                  value={rowLoc}
                  onChange={(e) => setRowLoc(e.target.value.toUpperCase())}
                  className="w-full border p-2 rounded-lg"
                />
              </div>
              <div>
                <label className="text-xs">Shed *</label>
                <input
                  value={sectionLoc}
                  onChange={(e) => setSectionLoc(e.target.value.toUpperCase())}
                  className="w-full border p-2 rounded-lg"
                />
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg border">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-sm">Initial Images</h3>
              <div>
                <input
                    id="initial-upload"
                    type="file"
                    accept="image/*"
                    capture="environment"
                    multiple
                    className="hidden"
                    onChange={onAddInitialImages}
                  />
                <label htmlFor="initial-upload" className="text-xs cursor-pointer text-blue-600">Add photos</label>
              </div>
            </div>

            {images.length > 0 && (
              <div className="grid grid-cols-4 gap-2">
                {images.map((img, i) => (
                  <div key={img.id || i} className="relative border rounded overflow-hidden">
                    <img src={img.preview} alt={`i-${i}`} className="w-full h-20 object-cover" />
                    <button onClick={() => removeInitialImage(i)} className="absolute top-1 right-1 text-white bg-black/60 rounded-full px-1">×</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button
            onClick={handleCreateBatchClick}
            disabled={creating || !finalQty || images.length === 0 || rowLoc === ""}
            className="w-full bg-blue-600 text-white p-3 rounded-lg disabled:opacity-50"
          >
            {creating ? "Creating..." : "Create Batch"}
          </button>
        </>
      ) : (
        <>
          <div className="bg-green-50 p-4 rounded-lg border text-center">
            <h3 className="font-bold">Batch Created</h3>
            <div className="mt-2">ID: <span className="font-mono">{batchResponse.batch_id}</span></div>
            <div className="inline-block mt-3 p-3 bg-white rounded">
              <QRCodeSVG id="qr-svg" value={`${batchResponse.batch_id}`} size={120} />
            </div>
            <canvas ref={qrCanvasRef} className="hidden" />
            <div className="mt-3">
              <button onClick={downloadQR} className="px-3 py-1 bg-green-600 text-white rounded text-sm">Download QR</button>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg border">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-sm">Submission Images</h3>
              <div>
                <input
                    id="submission-upload"
                    type="file"
                    accept="image/*"
                    capture="environment"
                    multiple
                    className="hidden"
                    onChange={onAddSubmissionImages}
                  />
                <label htmlFor="submission-upload" className="text-xs cursor-pointer text-orange-600">Upload</label>
              </div>
            </div>

            {submissionImages.length > 0 && (
              <div className="grid grid-cols-4 gap-2">
                {submissionImages.map((img, i) => (
                  <div key={img.id || i} className="relative border rounded overflow-hidden">
                    <img src={img.preview} alt={`s-${i}`} className="w-full h-20 object-cover" />
                    <button onClick={() => removeSubmissionImage(i)} className="absolute top-1 right-1 text-white bg-black/60 rounded-full px-1">×</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button
            onClick={completeBatch}
            disabled={completing || submissionImages.length === 0}
            className="w-full bg-green-600 text-white p-3 rounded-lg disabled:opacity-50"
          >
            {completing ? "Submitting..." : "Mark Tasks Completed"}
          </button>
        </>
      )}
    </div>
  </>
);

};

export default BatchSubmissionPage;
